import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "../lib/api";
import ContentDetail from "../components/ContentDetail";
import { StatusBadge, DataLabel } from "../components/common";
import { ShieldCheck, ArrowSquareOut } from "@phosphor-icons/react";

const mediaUrl = (it) => `${API}/public/media/${it.id}?v=${(it.updated_at || "").replace(/\D/g, "").slice(0, 14) || "1"}`;

export default function ValidationQueue() {
  const [items, setItems] = useState([]);
  const [recent, setRecent] = useState([]);
  const [selected, setSelected] = useState(null);
  const [params] = useSearchParams();

  const load = async () => {
    const [a, b, cc, pub] = await Promise.all([
      api.get("/content?status=pending_validation"),
      api.get("/content?status=needs_update"),
      api.get("/content?status=changes_requested"),
      api.get("/content/recent-published?limit=5"),
    ]);
    const merged = [...a.data, ...b.data, ...cc.data];
    setItems(merged);
    setRecent(pub.data || []);
    const deep = params.get("content");
    if (deep && merged.find((m) => m.id === deep)) { setSelected(deep); return; }
    if (!selected && merged.length) setSelected(merged[0].id);
    if (selected && !merged.find((m) => m.id === selected)) setSelected(merged[0]?.id || null);
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Human-in-the-loop</div>
        <h1 className="font-serif text-4xl text-brand tracking-tight">File de validation</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <DataLabel>{items.length} en attente</DataLabel>
          <div className="mt-3 space-y-2">
            {items.length === 0 && (
              <div className="bg-white border border-border rounded-md p-6 text-center">
                <ShieldCheck size={28} className="mx-auto text-emerald-500 mb-2" />
                <p className="text-sm text-muted-foreground">Rien à valider. Le moteur est à jour.</p>
              </div>
            )}
            {items.map((it) => (
              <button key={it.id} data-testid={`queue-item-${it.id}`} onClick={() => setSelected(it.id)}
                className={`w-full text-left bg-white border rounded-md p-3 transition-colors ${
                  selected === it.id ? "border-brand ring-1 ring-brand/20" : "border-border hover:border-brand/40"}`}>
                {it.image_path && (
                  <div className="mb-2.5 rounded overflow-hidden aspect-[16/9] bg-secondary">
                    <img src={mediaUrl(it)} alt="" loading="lazy" data-testid={`queue-thumb-${it.id}`}
                      className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-center justify-between mb-1.5">
                  <StatusBadge status={it.status} />
                  <span className="text-[10px] font-mono text-muted-foreground uppercase">{it.content_type}</span>
                </div>
                <div className="text-sm font-medium leading-snug line-clamp-2">{it.title_primary}</div>
              </button>
            ))}
          </div>

          {recent.length > 0 && (
            <div className="mt-6" data-testid="recently-published">
              <DataLabel>Publiés récemment</DataLabel>
              <div className="mt-3 space-y-2">
                {recent.map((it) => (
                  <div key={it.id} data-testid={`recent-${it.id}`}
                    className="bg-white border border-border rounded-md p-3">
                    <div className="text-sm font-medium leading-snug line-clamp-1">{it.title_primary}</div>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      {it.live_verified ? (
                        <span data-testid={`live-badge-${it.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                          <ShieldCheck size={11} weight="fill" /> En ligne ✓ · vérifié
                        </span>
                      ) : it.deployed ? (
                        <span data-testid={`live-badge-${it.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
                          <ShieldCheck size={11} weight="fill" /> En ligne ✓
                        </span>
                      ) : (
                        <span data-testid={`live-badge-${it.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-amber-50 text-amber-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> Déploiement en cours…
                        </span>
                      )}
                      {it.site_url && (
                        <a data-testid={`open-live-${it.id}`} href={it.site_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-mono text-ai hover:underline shrink-0"
                          title="Ouvrir la page réelle sur clinicavalorian.es">
                          <ArrowSquareOut size={12} /> Voir sur le site
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {selected ? (
            <div className="bg-bone">
              <ContentDetail contentId={selected} onChanged={load} />
            </div>
          ) : (
            <div className="bg-white border border-border rounded-md p-12 text-center text-sm text-muted-foreground">
              Sélectionnez un contenu à valider.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
